import { useState, type FormEvent } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, Field, Input, CenterStage } from '@projexn/ui';
import { authClient } from '../lib/auth-client';
import { hasActiveSession, refreshSessionAfterAuth } from '../lib/session';

type LoginSearch = { next?: string };

function resolveNextPath(next?: string): string {
  if (!next) return '/pipeline';
  if (next.startsWith('http://') || next.startsWith('https://')) {
    try {
      const url = new URL(next);
      return url.pathname + url.search;
    } catch {
      return '/pipeline';
    }
  }
  return next.startsWith('/') ? next : `/${next}`;
}

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as LoginSearch;
  const qc = useQueryClient();

  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result =
        mode === 'signin'
          ? await authClient.signIn.email({ email, password })
          : await authClient.signUp.email({ email, password, name: name || email });

      if (result.error) {
        setError(result.error.message ?? 'Authentication failed');
        return;
      }
      const session = await refreshSessionAfterAuth(qc);
      if (!hasActiveSession(session)) {
        setError('Signed in but no session cookie was stored. Clear site cookies and try again.');
        return;
      }
      const next = resolveNextPath(search.next);
      await navigate({ to: next, replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <CenterStage>
      <Card className="w-full max-w-sm p-6">
        <div className="mb-6">
          <h1 className="text-xl font-semibold">Tender Management</h1>
          <p className="text-sm text-zinc-500">
            {mode === 'signin' ? 'Sign in to your tenant.' : 'Create your account.'}
          </p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          {mode === 'signup' && (
            <Field label="Name">
              <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </Field>
          )}
          <Field label="Email" required>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </Field>
          <Field label="Password" required>
            <Input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            />
          </Field>
          {error && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          )}
          <Button type="submit" loading={submitting} className="w-full">
            {mode === 'signin' ? 'Sign in' : 'Create account'}
          </Button>
        </form>
        <button
          type="button"
          onClick={() => {
            setMode(mode === 'signin' ? 'signup' : 'signin');
            setError(null);
          }}
          className="mt-4 w-full text-center text-sm text-zinc-500 hover:text-zinc-700"
        >
          {mode === 'signin' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
        </button>
      </Card>
    </CenterStage>
  );
}
